import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'

/** Cuenta que se estaba conectando; la deja Tesorería antes de salir al banco. */
export const PENDING_KEY = 'jr2os.banking.connectingAccountId'

/**
 * Vuelta del banco tras autorizar el acceso a la cuenta (PSD2).
 *
 * El banco redirige aquí con `code` y `state`; esta página los canjea por una
 * sesión llamando al backend y devuelve al usuario a Tesorería. Va DENTRO de
 * AuthGuard a propósito: el canje exige rol ADMIN, y quien vuelve del banco es
 * el mismo usuario que inició la conexión, que ya tenía sesión abierta.
 */
export default function BankingCallback() {
  const { t } = useTranslation('banking')
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<'working' | 'ok' | 'error'>('working')
  const [message, setMessage] = useState<string | null>(null)
  // El canje solo puede ocurrir UNA vez: el código es de un solo uso y en
  // desarrollo React monta dos veces, lo que gastaría el código sin querer.
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    done.current = true
    const code = params.get('code')
    const bankError = params.get('error')
    // El proveedor solo GARANTIZA devolver `code`; que reenvíe `state` no está
    // documentado. Antes exigíamos los dos y, si faltaba `state`, decíamos "no
    // devolvió el código" — mensaje falso y diagnóstico imposible. Ahora la
    // cuenta se recuerda al salir hacia el banco y `state` es solo un refuerzo.
    const accountId = params.get('state') || sessionStorage.getItem(PENDING_KEY)

    if (bankError) {
      setState('error')
      setMessage(t('callback.deniedAtBank'))
      return
    }
    if (!code) {
      setState('error')
      // Se muestra lo que SÍ llegó: sin esto no hay forma de saber qué pasó.
      const got = Array.from(params.keys())
      setMessage(
        got.length > 0 ? t('callback.missingCodeGot', { params: got.join(', ') }) : t('callback.missingCode')
      )
      return
    }
    if (!accountId) {
      setState('error')
      setMessage(t('callback.missingAccount'))
      return
    }
    sessionStorage.removeItem(PENDING_KEY)
    void (async () => {
      try {
        await api.post('/banking/feed/callback', { code, state: accountId })
        setState('ok')
        // Sincroniza en cuanto se conecta: ver movimientos es la prueba de que
        // ha funcionado, mucho mejor que un "conectado" a secas.
        try {
          await api.post(`/banking/accounts/${accountId}/sync`, {})
        } catch {
          /* si falla, la cuenta ya está conectada y se puede reintentar */
        }
        setTimeout(() => navigate('/banking'), 1500)
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
        setState('error')
        setMessage(body?.error ?? (err instanceof Error ? err.message : t('callback.generic')))
      }
    })()
  }, [params, navigate, t])

  return (
    <div className="max-w-md mx-auto mt-16 surface p-6 text-center space-y-3 animate-slideUp">
      {state === 'working' && (
        <>
          <Loader2 size={24} className="animate-spin mx-auto t-muted" />
          <p className="text-sm t-secondary">{t('callback.working')}</p>
        </>
      )}
      {state === 'ok' && (
        <>
          <CheckCircle2 size={24} className="mx-auto text-emerald-500" />
          <p className="text-sm t-primary font-semibold">{t('callback.ok')}</p>
          <p className="text-xs t-muted">{t('callback.redirecting')}</p>
        </>
      )}
      {state === 'error' && (
        <>
          <AlertCircle size={24} className="mx-auto text-rose-500" />
          <p className="text-sm t-primary font-semibold">{t('callback.failed')}</p>
          {message && <p className="text-xs t-secondary">{message}</p>}
          <button
            type="button"
            onClick={() => navigate('/banking')}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('callback.back')}
          </button>
        </>
      )}
    </div>
  )
}
